import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ActionsDropdown from './ActionsDropdown';
import useFetch from '../../hooks/useFetch';
import styles from '../../styles/equipos.module.css';

const BASE_EMPRESAS = '/gestionEmpresas';
const BASE_EQUIPOS = '/gestionEquipos';

const Equipos = () => {
    const navigate = useNavigate();
    const { execute, loading, error } = useFetch();
    const [empresas, setEmpresas] = useState([]);
    const [expanded, setExpanded] = useState({});

    // Modales empresa
    const [empresaModal, setEmpresaModal] = useState(null); // { mode: 'create' | 'edit', empresa }
    const [empresaNombre, setEmpresaNombre] = useState('');
    const [empresaFormError, setEmpresaFormError] = useState('');
    const [deleteEmpresa, setDeleteEmpresa] = useState(null);

    // Modales equipo
    const [equipoModal, setEquipoModal] = useState(null); // { mode, empresa, equipo }
    const [equipoNombre, setEquipoNombre] = useState('');
    const [equipoFormError, setEquipoFormError] = useState('');
    const [deleteEquipo, setDeleteEquipo] = useState(null);

    const fetchEmpresas = useCallback(async () => {
        try {
            const res = await execute(`${BASE_EMPRESAS}/empresas`, 'GET');
            setEmpresas(Array.isArray(res?.data) ? res.data : []);
        } catch {
            setEmpresas([]);
        }
    }, [execute]);

    useEffect(() => {
        fetchEmpresas();
    }, [fetchEmpresas]);

    const toggleExpand = (id_empresa) => {
        setExpanded((prev) => ({ ...prev, [id_empresa]: !(prev[id_empresa] ?? true) }));
    };

    const goToMembers = (equipo) => {
        navigate(`/creatividades/equipos/${equipo.id_equipo}`);
    };

    // ---- Empresa CRUD ----
    const openCreateEmpresa = () => {
        setEmpresaModal({ mode: 'create' });
        setEmpresaNombre('');
        setEmpresaFormError('');
    };

    const openEditEmpresa = (empresa) => {
        setEmpresaModal({ mode: 'edit', empresa });
        setEmpresaNombre(empresa.nombre || '');
        setEmpresaFormError('');
    };

    const handleSaveEmpresa = async (e) => {
        e.preventDefault();
        setEmpresaFormError('');
        const nombreTrim = empresaNombre.trim();
        if (!nombreTrim) {
            setEmpresaFormError('El nombre de la empresa es obligatorio.');
            return;
        }
        try {
            if (empresaModal?.mode === 'edit') {
                await execute(`${BASE_EMPRESAS}/editarEmpresa`, 'PUT', {
                    id_empresa: empresaModal.empresa.id_empresa,
                    nombre: nombreTrim,
                });
            } else {
                await execute(`${BASE_EMPRESAS}/crearEmpresa`, 'POST', { nombre: nombreTrim });
            }
            setEmpresaModal(null);
            await fetchEmpresas();
        } catch (err) {
            setEmpresaFormError(err?.message || 'Error al guardar la empresa.');
        }
    };

    const handleDeleteEmpresa = async () => {
        if (!deleteEmpresa) return;
        try {
            await execute(`${BASE_EMPRESAS}/eliminarEmpresa`, 'POST', { id_empresa: deleteEmpresa.id_empresa });
            setDeleteEmpresa(null);
            await fetchEmpresas();
        } catch {
            // gestionado por useFetch
        }
    };

    // ---- Equipo CRUD ----
    const openCreateEquipo = (empresa) => {
        setEquipoModal({ mode: 'create', empresa });
        setEquipoNombre('');
        setEquipoFormError('');
    };

    const openEditEquipo = (empresa, equipo) => {
        setEquipoModal({ mode: 'edit', empresa, equipo });
        setEquipoNombre(equipo.nombre_equipo || '');
        setEquipoFormError('');
    };

    const handleSaveEquipo = async (e) => {
        e.preventDefault();
        setEquipoFormError('');
        const nombreTrim = equipoNombre.trim();
        if (!nombreTrim) {
            setEquipoFormError('El nombre del equipo es obligatorio.');
            return;
        }
        try {
            if (equipoModal?.mode === 'edit') {
                await execute(`${BASE_EQUIPOS}/editarEquipo`, 'PUT', {
                    id_equipo: equipoModal.equipo.id_equipo,
                    nombre_equipo: nombreTrim,
                });
            } else {
                await execute(`${BASE_EQUIPOS}/crearEquipo`, 'POST', {
                    id_empresa: equipoModal.empresa.id_empresa,
                    nombre_equipo: nombreTrim,
                });
                setExpanded((prev) => ({ ...prev, [equipoModal.empresa.id_empresa]: true }));
            }
            setEquipoModal(null);
            await fetchEmpresas();
        } catch (err) {
            setEquipoFormError(err?.message || 'Error al guardar el equipo.');
        }
    };

    const handleDeleteEquipo = async () => {
        if (!deleteEquipo) return;
        try {
            await execute(`${BASE_EQUIPOS}/eliminarEquipo`, 'POST', { id_equipo: deleteEquipo.equipo.id_equipo });
            setDeleteEquipo(null);
            await fetchEmpresas();
        } catch {
            // gestionado por useFetch
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h2 className={styles.title}>Empresas y equipos</h2>
                    <p className={styles.subtitle}>Crea empresas, organiza sus equipos y gestiona los miembros de cada uno.</p>
                </div>
                <button type="button" className={styles.createBtn} onClick={openCreateEmpresa}>
                    <span className={styles.createBtnIcon} aria-hidden>+</span>
                    Nueva empresa
                </button>
            </div>

            {error?.message && <p className={styles.errorMsg} role="alert">{error.message}</p>}

            {loading && empresas.length === 0 ? (
                <p className={styles.loadingMsg}>Cargando empresas…</p>
            ) : empresas.length === 0 ? (
                <div className={styles.emptyCompanies}>
                    <p>Todavía no hay empresas.</p>
                    <p>Crea la primera empresa para empezar a organizar equipos.</p>
                </div>
            ) : (
                <div className={styles.companyList}>
                    {empresas.map((empresa) => {
                        const isOpen = expanded[empresa.id_empresa] ?? true;
                        const equipos = empresa.equipos || [];
                        return (
                            <div key={empresa.id_empresa} className={styles.companyCard}>
                                <div className={styles.companyHeader}>
                                    <button
                                        type="button"
                                        className={styles.companyToggle}
                                        onClick={() => toggleExpand(empresa.id_empresa)}
                                        aria-expanded={isOpen}
                                    >
                                        <span className={`${styles.companyChevron} ${isOpen ? styles.companyChevronOpen : ''}`} aria-hidden>▸</span>
                                        <span className={styles.companyName}>{empresa.nombre}</span>
                                        <span className={styles.companyBadge}>{equipos.length} {equipos.length === 1 ? 'equipo' : 'equipos'}</span>
                                    </button>
                                    <div className={styles.companyActions}>
                                        <button type="button" className={styles.companyAddTeamBtn} onClick={() => openCreateEquipo(empresa)}>
                                            <span aria-hidden>+</span> Equipo
                                        </button>
                                        <ActionsDropdown
                                            actions={[
                                                { label: 'Editar empresa', onClick: () => openEditEmpresa(empresa) },
                                                { label: 'Eliminar empresa', onClick: () => setDeleteEmpresa(empresa), danger: true },
                                            ]}
                                        />
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className={styles.teamList}>
                                        {equipos.length === 0 ? (
                                            <div className={styles.teamEmpty}>
                                                <p>Esta empresa todavía no tiene equipos.</p>
                                                <button type="button" className={styles.teamEmptyBtn} onClick={() => openCreateEquipo(empresa)}>
                                                    Crear primer equipo
                                                </button>
                                            </div>
                                        ) : (
                                            equipos.map((equipo) => (
                                                <div
                                                    key={equipo.id_equipo}
                                                    className={styles.teamRow}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => goToMembers(equipo)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToMembers(equipo); } }}
                                                >
                                                    <div className={styles.teamInfo}>
                                                        <span className={styles.teamName}>{equipo.nombre_equipo}</span>
                                                        <span className={styles.teamMeta}>{equipo.num_miembros ?? 0} {(equipo.num_miembros ?? 0) === 1 ? 'miembro' : 'miembros'}</span>
                                                    </div>
                                                    <div className={styles.teamActions} onClick={(e) => e.stopPropagation()}>
                                                        {equipo.tiene_encuesta_activa && (
                                                            <span className={styles.teamSurveyBadge}>Encuesta activa</span>
                                                        )}
                                                        <ActionsDropdown
                                                            actions={[
                                                                { label: 'Gestionar miembros', onClick: () => goToMembers(equipo) },
                                                                { label: 'Editar equipo', onClick: () => openEditEquipo(empresa, equipo) },
                                                                { label: 'Eliminar equipo', onClick: () => setDeleteEquipo({ empresa, equipo }), danger: true },
                                                            ]}
                                                        />
                                                        <span className={styles.teamChevron} aria-hidden>›</span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal empresa */}
            {empresaModal && (
                <div className={styles.modalOverlay} onClick={() => setEmpresaModal(null)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>{empresaModal.mode === 'edit' ? 'Editar empresa' : 'Nueva empresa'}</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setEmpresaModal(null)} aria-label="Cerrar">✕</button>
                        </div>
                        <form onSubmit={handleSaveEmpresa} className={styles.form}>
                            {empresaFormError && <p className={styles.errorMsg} role="alert">{empresaFormError}</p>}
                            <div className={styles.inputGroup}>
                                <label htmlFor="empresa-nombre">Nombre de la empresa</label>
                                <input
                                    id="empresa-nombre"
                                    type="text"
                                    value={empresaNombre}
                                    onChange={(e) => setEmpresaNombre(e.target.value)}
                                    required
                                    maxLength={150}
                                    placeholder="Ej. Acme S.L."
                                    autoFocus
                                />
                            </div>
                            <div className={styles.formActions}>
                                <button type="button" className={styles.actionBtn} onClick={() => setEmpresaModal(null)}>Cancelar</button>
                                <button type="submit" className={styles.submitBtn}>{empresaModal.mode === 'edit' ? 'Guardar cambios' : 'Crear empresa'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal eliminar empresa */}
            {deleteEmpresa && (
                <div className={styles.modalOverlay} onClick={() => setDeleteEmpresa(null)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Eliminar empresa</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setDeleteEmpresa(null)} aria-label="Cerrar">✕</button>
                        </div>
                        <p className={styles.confirmText}>
                            ¿Eliminar la empresa &quot;{deleteEmpresa.nombre}&quot;? Se eliminarán también sus {(deleteEmpresa.equipos || []).length} equipo(s) y sus asignaciones de miembros. Esta acción no se puede deshacer.
                        </p>
                        <div className={styles.formActions}>
                            <button type="button" className={styles.actionBtn} onClick={() => setDeleteEmpresa(null)}>Cancelar</button>
                            <button type="button" className={styles.dangerBtn} onClick={handleDeleteEmpresa}>Eliminar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal equipo */}
            {equipoModal && (
                <div className={styles.modalOverlay} onClick={() => setEquipoModal(null)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>{equipoModal.mode === 'edit' ? 'Editar equipo' : 'Nuevo equipo'}</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setEquipoModal(null)} aria-label="Cerrar">✕</button>
                        </div>
                        <form onSubmit={handleSaveEquipo} className={styles.form}>
                            {equipoFormError && <p className={styles.errorMsg} role="alert">{equipoFormError}</p>}
                            {equipoModal.empresa && (
                                <p className={styles.modalContextNote}>Empresa: <strong>{equipoModal.empresa.nombre}</strong></p>
                            )}
                            <div className={styles.inputGroup}>
                                <label htmlFor="equipo-nombre">Nombre del equipo</label>
                                <input
                                    id="equipo-nombre"
                                    type="text"
                                    value={equipoNombre}
                                    onChange={(e) => setEquipoNombre(e.target.value)}
                                    required
                                    maxLength={120}
                                    placeholder="Ej. Comercial España"
                                    autoFocus
                                />
                                <span className={styles.inputHint}>Máximo 120 caracteres</span>
                            </div>
                            <div className={styles.formActions}>
                                <button type="button" className={styles.actionBtn} onClick={() => setEquipoModal(null)}>Cancelar</button>
                                <button type="submit" className={styles.submitBtn}>{equipoModal.mode === 'edit' ? 'Guardar cambios' : 'Crear equipo'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal eliminar equipo */}
            {deleteEquipo && (
                <div className={styles.modalOverlay} onClick={() => setDeleteEquipo(null)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Eliminar equipo</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setDeleteEquipo(null)} aria-label="Cerrar">✕</button>
                        </div>
                        <p className={styles.confirmText}>¿Eliminar el equipo &quot;{deleteEquipo.equipo.nombre_equipo}&quot;? Esta acción no se puede deshacer.</p>
                        <div className={styles.formActions}>
                            <button type="button" className={styles.actionBtn} onClick={() => setDeleteEquipo(null)}>Cancelar</button>
                            <button type="button" className={styles.dangerBtn} onClick={handleDeleteEquipo}>Eliminar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Equipos;
